import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../config/supabase';
import { useAuth } from '../../contexts/AuthContext';
import { ArrowLeftIcon } from '../../components/icons/NavIcons';
import { useAdminCheck } from '../../hooks/useAdminCheck';
import LoadingSpinner from '../../components/LoadingSpinner';
import PageHeader from '../../components/PageHeader';

const EventApprovalPage = () => {
  const { isAdmin, loading: adminLoading } = useAdminCheck();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [rejectionMessage, setRejectionMessage] = useState('');
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [processingEventId, setProcessingEventId] = useState(null);

  useEffect(() => {
    fetchPendingEvents();
  }, []);

  const fetchPendingEvents = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('events')
        .select(`
          *,
          categories(name),
          age_groups(name)
        `)
        .eq('status', 'pending_approval')
        .order('created_at', { ascending: false });

      if (error) throw error;

      // Récupérer les profils des organisateurs avec leurs emails et les transactions
      const eventsWithProfiles = await Promise.all(
        data.map(async (event) => {
          if (event.organizer_id) {
            const [profileRes, creditsRes, transactionRes] = await Promise.all([
              supabase
                .from('profiles')
                .select('id, organizer_name, organizer_lastname, organizer_firstname, organizer_company_name, organizer_email, organizer_url')
                .eq('id', event.organizer_id)
                .single(),
              supabase
                .from('credits')
                .select('credits')
                .eq('id', event.organizer_id)
                .single(),
              supabase
                .from('transactions')
                .select('*')
                .eq('event_id', event.id)
                .order('created_at', { ascending: false })
                .limit(1)
                .maybeSingle()
            ]);
            
            return { 
              ...event, 
              organizerProfile: {
                ...profileRes.data,
                credits: creditsRes.data?.credits || 0
              },
              transaction: transactionRes.data
            };
          }
          return event;
        })
      );

      setEvents(eventsWithProfiles);
    } catch (error) {
      console.error('Erreur lors du chargement des événements:', error);
      alert(t('settings.admin.eventApproval.loadError'));
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (eventId) => {
    try {
      setProcessingEventId(eventId);
      const { error } = await supabase
        .from('events')
        .update({ 
          status: 'published',
          published_at: new Date().toISOString()
        })
        .eq('id', eventId);

      if (error) throw error;

      fetchPendingEvents();
    } catch (error) {
      console.error('Erreur lors de l\'approbation:', error);
      alert(t('settings.admin.eventApproval.approveError'));
    } finally {
      setProcessingEventId(null);
    }
  };

  const openRejectModal = (event) => {
    setSelectedEvent(event);
    setRejectionMessage('');
    setShowRejectModal(true);
  };

  const handleReject = async () => {
    if (!rejectionMessage.trim()) {
      alert(t('settings.admin.eventApproval.messageRequired'));
      return;
    }

    try {
      setProcessingEventId(selectedEvent.id);
      const { error } = await supabase
        .from('events')
        .update({ 
          status: 'draft',
          message: rejectionMessage
        })
        .eq('id', selectedEvent.id);

      if (error) throw error;

      setShowRejectModal(false);
      setSelectedEvent(null);
      setRejectionMessage('');
      fetchPendingEvents();
    } catch (error) {
      console.error('Erreur lors du refus:', error);
      alert(t('settings.admin.eventApproval.rejectError'));
    } finally {
      setProcessingEventId(null);
    }
  };

  const formatDate = (date) => {
    if (!date) return '-';
    return new Date(date).toLocaleDateString();
  };

  const formatTime = (time) => {
    if (!time) return '-';
    return time.substring(0, 5);
  };

  if (adminLoading) {
    return <LoadingSpinner fullScreen />;
  }

  if (loading) {
    return <LoadingSpinner fullScreen />;
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-20">
      <PageHeader title={t('settings.admin.eventApproval.title')} backPath="/settings" />

      <div className="p-4">
        {events.length === 0 ? (
          <div className="bg-white p-6 rounded-lg shadow-sm text-center">
            <p className="text-gray-500">{t('settings.admin.eventApproval.noEvents')}</p>
          </div>
        ) : (
          <div className="space-y-4">
            {events.map((event) => (
              <div key={event.id} className="bg-white rounded-lg shadow-sm overflow-hidden">
                {/* Image de l'événement */}
                {event.image_url && (
                  <img 
                    src={event.image_url} 
                    alt={event.title}
                    className="w-full h-48 object-cover"
                  />
                )}

                <div className="p-4">
                  {/* Titre et catégorie */}
                  <h3 className="text-h3 mb-2">{event.title}</h3>
                  <p className="text-sm text-gray-600 mb-4">{event.description}</p>

                  {/* Informations de l'événement */}
                  <div className="space-y-2 text-sm mb-4">
                    <div className="flex">
                      <span className="font-semibold w-32">{t('filters.categories')}</span>
                      <span>{event.categories?.name || '-'}</span>
                    </div>
                    <div className="flex">
                      <span className="font-semibold w-32">{t('event.ageGroup')}</span>
                      <span>{event.age_groups?.name || '-'}</span>
                    </div>
                    <div className="flex">
                      <span className="font-semibold w-32">{t('filters.date')}</span>
                      <span>{formatDate(event.start_date)} - {formatDate(event.end_date)}</span>
                    </div>
                    <div className="flex">
                      <span className="font-semibold w-32">{t('event.time')}</span>
                      <span>{formatTime(event.start_time)} - {formatTime(event.end_time)}</span>
                    </div>
                    <div className="flex">
                      <span className="font-semibold w-32">{t('event.location')}</span>
                      <span>{event.formatted_address || event.location_name || '-'}</span>
                    </div>
                    <div className="flex">
                      <span className="font-semibold w-32">{t('event.city')}</span>
                      <span>{event.city || '-'}</span>
                    </div>
                    <div className="flex">
                      <span className="font-semibold w-32">{t('event.postalCode')}</span>
                      <span>{event.postal_code || '-'}</span>
                    </div>
                    <div className="flex">
                      <span className="font-semibold w-32">{t('event.coordinates')}</span>
                      <span>
                        {event.latitude && event.longitude 
                          ? `${event.latitude.toFixed(6)}, ${event.longitude.toFixed(6)}`
                          : '-'}
                      </span>
                    </div>
                    <div className="flex">
                      <span className="font-semibold w-32">{t('event.paid')}</span>
                      <span>{event.is_paid ? t('common.yes') : t('common.no')}</span>
                    </div>
                    <div className="flex">
                      <span className="font-semibold w-32">{t('event.accessible')}</span>
                      <span>{event.is_accessible ? t('common.yes') : t('common.no')}</span>
                    </div>
                    {event.external_url && (
                      <div className="flex">
                        <span className="font-semibold w-32">{t('event.externalURL')}</span>
                        <a href={event.external_url} target="_blank" rel="noopener noreferrer" className="text-blue-500 hover:underline break-all">
                          {event.external_url}
                        </a>
                      </div>
                    )}
                    {event.image_url && (
                      <div className="flex">
                        <span className="font-semibold w-32">{t('event.imageURL')}</span>
                        <a href={event.image_url} target="_blank" rel="noopener noreferrer" className="text-blue-500 hover:underline break-all">
                          {event.image_url}
                        </a>
                      </div>
                    )}
                  </div>

                  {/* Informations de l'organisateur */}
                  <div className="border-t pt-4 mt-4">
                    <h4 className="text-h3 mb-2">{t('settings.admin.eventApproval.organizerInfo')}</h4>
                    <div className="space-y-2 text-sm">
                      {event.organizerProfile && (
                        <>
                          <div className="flex">
                            <span className="font-semibold w-32">{t('profile.organizerName')}</span>
                            <span>{event.organizerProfile.organizer_name || '-'}</span>
                          </div>
                          <div className="flex">
                            <span className="font-semibold w-32">{t('profile.organizerLastname')}</span>
                            <span>{event.organizerProfile.organizer_lastname || '-'}</span>
                          </div>
                          <div className="flex">
                            <span className="font-semibold w-32">{t('profile.organizerFirstname')}</span>
                            <span>{event.organizerProfile.organizer_firstname || '-'}</span>
                          </div>
                          <div className="flex">
                            <span className="font-semibold w-32">{t('profile.organizerCompanyName')}</span>
                            <span>{event.organizerProfile.organizer_company_name || '-'}</span>
                          </div>
                          <div className="flex">
                            <span className="font-semibold w-32">{t('profile.organizerEmail')}</span>
                            {event.organizerProfile.organizer_email ? (
                              <a href={`mailto:${event.organizerProfile.organizer_email}`} className="text-blue-500 hover:underline">
                                {event.organizerProfile.organizer_email}
                              </a>
                            ) : (
                              <span>-</span>
                            )}
                          </div>
                          <div className="flex">
                            <span className="font-semibold w-32">{t('profile.organizerUrl')}</span>
                            {event.organizerProfile.organizer_url ? (
                              <a href={event.organizerProfile.organizer_url} target="_blank" rel="noopener noreferrer" className="text-blue-500 hover:underline break-all">
                                {event.organizerProfile.organizer_url}
                              </a>
                            ) : (
                              <span>-</span>
                            )}
                          </div>
                          <div className="flex">
                            <span className="font-semibold w-32">{t('settings.admin.eventApproval.credits')}</span>
                            <span>{event.organizerProfile.credits || 0}</span>
                          </div>
                        </>
                      )}
                    </div>
                  </div>

                  {/* Informations de transaction */}
                  <div className="border-t pt-4 mt-4">
                    <h4 className="text-h3 mb-2">{t('settings.admin.eventApproval.transactionInfo')}</h4>
                    {event.transaction ? (
                      <div className="space-y-2 text-sm">
                        <div className="flex">
                          <span className="font-semibold w-32">{t('settings.admin.eventApproval.transactionId')}</span>
                          <span className="font-mono text-xs">{event.transaction.id}</span>
                        </div>
                        <div className="flex">
                          <span className="font-semibold w-32">{t('settings.admin.eventApproval.transactionType')}</span>
                          <span>{event.transaction.type}</span>
                        </div>
                        <div className="flex">
                          <span className="font-semibold w-32">{t('settings.admin.eventApproval.transactionAmount')}</span>
                          <span>{event.transaction.amount} €</span>
                        </div>
                        <div className="flex">
                          <span className="font-semibold w-32">{t('settings.admin.eventApproval.transactionCredits')}</span>
                          <span>{event.transaction.credits || '-'}</span>
                        </div>
                        <div className="flex">
                          <span className="font-semibold w-32">{t('settings.admin.eventApproval.transactionStatus')}</span>
                          <span className={`px-2 py-1 rounded ${
                            event.transaction.status === 'completed' ? 'bg-green-100 text-green-800' : 
                            event.transaction.status === 'pending' ? 'bg-yellow-100 text-yellow-800' : 
                            'bg-red-100 text-red-800'
                          }`}>
                            {event.transaction.status}
                          </span>
                        </div>
                        <div className="flex">
                          <span className="font-semibold w-32">{t('settings.admin.eventApproval.transactionDate')}</span>
                          <span>{formatDate(event.transaction.created_at)}</span>
                        </div>
                        {event.transaction.description && (
                          <div className="flex">
                            <span className="font-semibold w-32">{t('settings.admin.eventApproval.transactionDescription')}</span>
                            <span>{event.transaction.description}</span>
                          </div>
                        )}
                        {event.transaction.stripe_session_id && (
                          <div className="flex">
                            <span className="font-semibold w-32">{t('settings.admin.eventApproval.stripeSessionId')}</span>
                            <span className="font-mono text-xs break-all">{event.transaction.stripe_session_id}</span>
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                        <p className="text-red-800 font-semibold">
                          ⚠️ {t('settings.admin.eventApproval.missingTransaction')}
                        </p>
                        <p className="text-red-600 text-sm mt-1">
                          {t('settings.admin.eventApproval.missingTransactionDescription')}
                        </p>
                      </div>
                    )}
                  </div>

                  {/* Boutons d'action */}
                  <div className="flex gap-3 mt-6">
                    <button
                      onClick={() => handleApprove(event.id)}
                      disabled={processingEventId === event.id}
                      className="flex-1 bg-green-500 text-white py-3 rounded-lg font-medium hover:bg-green-600 disabled:opacity-50"
                    >
                      {processingEventId === event.id ? t('common.loading') : t('settings.admin.eventApproval.approve')}
                    </button>
                    <button
                      onClick={() => openRejectModal(event)}
                      disabled={processingEventId === event.id}
                      className="flex-1 bg-red-500 text-white py-3 rounded-lg font-medium hover:bg-red-600 disabled:opacity-50"
                    >
                      {t('settings.admin.eventApproval.reject')}
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Modal de refus */}
      {showRejectModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <h3 className="text-h2 mb-4">{t('settings.admin.eventApproval.rejectTitle')}</h3>
            <p className="text-sm text-gray-600 mb-4">{t('settings.admin.eventApproval.rejectDescription')}</p>
            
            <textarea
              value={rejectionMessage}
              onChange={(e) => setRejectionMessage(e.target.value)}
              placeholder={t('settings.admin.eventApproval.messagePlaceholder')}
              className="w-full border border-gray-300 rounded-lg p-3 mb-4 min-h-[120px]"
              required
            />

            <div className="flex gap-3">
              <button
                onClick={() => setShowRejectModal(false)}
                disabled={processingEventId}
                className="flex-1 bg-gray-200 text-gray-800 py-2 rounded-lg font-medium hover:bg-gray-300"
              >
                {t('common.cancel')}
              </button>
              <button
                onClick={handleReject}
                disabled={processingEventId || !rejectionMessage.trim()}
                className="flex-1 bg-red-500 text-white py-2 rounded-lg font-medium hover:bg-red-600 disabled:opacity-50"
              >
                {processingEventId ? t('common.loading') : t('settings.admin.eventApproval.confirmReject')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EventApprovalPage;
